import { useState, useEffect } from 'react';
import { Box, Button, Container, Grid, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { LoadingButton } from '@mui/lab';
import BreadCrumb from '../../components/breadcrumb/BreadCrumbNavigation';
import { useStyles } from './quarterGoalPageStyles'; // Import the styles
import { isAdmin } from '../../utils/share';
import Iconify from '../../components/iconify';

import NoDataComponent from '../../common/NoDataComponent';
import {
  getAllQuarterGoals,
  deleteQuarterGoal,
  togglePublishQuarterGoal,
} from '../../services/companyQuarterGoalsServices';
import Loading from '../../layouts/loading';
import { DeleteModal } from '../clients/components';
import QuarterGoalDrawer from './QuarterGoalDrawer'; // Import the drawer component
import { FilterSectionComponent } from './FilterSectionComponent';

const GoalItem = ({ goal, isAdminFlag, toggleLoading, handleTogglePublish, handleEdit, handleDelete, classes }) => (
  <Box key={goal.id} mb={4} p={2} className={classes.goalBox} position="relative">
    <Typography variant="h5">{goal.title}</Typography>
    <Typography className={classes.description} variant="body1" dangerouslySetInnerHTML={{ __html: goal.description }} />
    <Box
      sx={{
        position: 'absolute',
        top: 16, // Position at the top of the description box
        right: 16, // Position at the right of the description box
        display: 'flex',
        gap: 1,
      }}
    >
      <LoadingButton
        loading={toggleLoading}
        disabled={!isAdminFlag || toggleLoading}
        className={classes?.actionBtn}
        size="small"
        onClick={() => handleTogglePublish(goal.id, goal.isPublished)}
        startIcon={
          goal.isPublished ? <Iconify icon="eva:undo-outline" /> : <Iconify icon="eva:checkmark-circle-2-outline" />
        }
      >
        {goal.isPublished ? 'Rollback' : 'Publish'}
      </LoadingButton>
      <Button
        disabled={!isAdminFlag}
        className={classes?.actionBtn}
        size="small"
        onClick={() => handleEdit(goal)}
        startIcon={<Iconify icon="eva:edit-fill" />}
      >
        Edit
      </Button>
      <Button
        disabled={!isAdminFlag}
        className={classes?.actionBtn}
        color="error"
        size="small"
        onClick={() => handleDelete(goal.id)}
        startIcon={<Iconify icon="eva:trash-2-outline" />}
      >
        Delete
      </Button>
    </Box>
  </Box>
);

export const QuarterGoalPage = () => {
  const classes = useStyles();
  const breadcrumbs = [
    { label: 'Home', path: '/' },
    { label: 'Quarterly Goal', path: '' },
  ];
  const isAdminFlag = isAdmin();

  // Calculate the current year and quarter
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;
  const currentQuarter = Math.ceil(currentMonth / 3);

  // State variables
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedQuarter, setSelectedQuarter] = useState(`Q${currentQuarter}`);
  const [quarterGoals, setQuarterGoals] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // State for delete functionality
  const [deleteGoalId, setDeleteGoalId] = useState(null); // Stores the ID of the goal to delete

  // Fetch quarter goals on component mount
  useEffect(() => {
    fetchQuarterGoalData(selectedYear, selectedQuarter);
  }, [selectedYear, selectedQuarter]);

  // Fetch quarter goals from the API
  const fetchQuarterGoalData = async (year, quarter) => {
    setIsLoading(true);
    try {
      const response = await getAllQuarterGoals({ year, quarter });
      setQuarterGoals(response?.data?.data || []);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to fetch quarter goals');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle year filter change
  const handleYearChange = (event, newValue) => {
    setSelectedYear(newValue);
  };

  // Handle quarter filter change
  const handleQuarterChange = (event, newValue) => {
    setSelectedQuarter(newValue.value); // Use the 'value' property of the selected quarter
  };

  // Handle delete confirmation
  const handleDelete = async (goalId) => {
    setDeleteGoalId(goalId);
  };

  // Handle delete action
  const handleDeleteConfirm = async () => {
    if (!deleteGoalId) return;

    try {
      await deleteQuarterGoal(deleteGoalId);
      toast.success('Goal deleted successfully');
      setQuarterGoals((prevGoals) => prevGoals.filter((goal) => goal.id !== deleteGoalId));
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to delete goal');
      console.error(error);
    } finally {
      setDeleteGoalId(null);
    }
  };

  // Handle toggle publish/rollback functionality
  const handleTogglePublish = async (goalId, currentStatus) => {
    setToggleLoading(true);
    try {
      await togglePublishQuarterGoal(goalId);
      toast.success(currentStatus ? 'Goal rolled back successfully' : 'Goal published successfully');
      setQuarterGoals((prevGoals) =>
        prevGoals.map((goal) => (goal.id === goalId ? { ...goal, isPublished: !goal.isPublished } : goal))
      );
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to toggle goal status');
      console.error(error);
    } finally {
      setToggleLoading(false);
    }
  };

  // Handle edit functionality
  const handleEdit = (goal) => {
    setSelectedGoal(goal);
    setIsEditing(true);
    setDrawerOpen(true);
  };

  // Handle add functionality
  const handleAddGoal = () => {
    setSelectedGoal(null);
    setIsEditing(false);
    setDrawerOpen(true);
  };

  // Handle drawer close
  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedGoal(null);
    setIsEditing(false);
  };

  // Handle drawer save
  const handleDrawerSave = () => {
    fetchQuarterGoalData(selectedYear, selectedQuarter);
  };

  return (
    <Container maxWidth="xl" className={classes.container}>
      <Box>
        <BreadCrumb breadcrumbs={breadcrumbs} />
        <FilterSectionComponent
          selectedYear={selectedYear}
          selectedQuarter={selectedQuarter}
          handleYearChange={handleYearChange}
          handleQuarterChange={handleQuarterChange}
          isAdminFlag={isAdminFlag}
          handleAddGoal={handleAddGoal}
          addButtonShow
        />

        {/* Conditionally render the data or the NoDataComponent */}
        <Box mt={4}>
          {isLoading ? (
            <Loading />
          ) : quarterGoals.length > 0 ? (
            quarterGoals.map((goal) => (
              <GoalItem
                key={goal.id}
                goal={goal}
                isAdminFlag={isAdminFlag}
                toggleLoading={toggleLoading}
                handleTogglePublish={handleTogglePublish}
                handleEdit={handleEdit}
                handleDelete={handleDelete}
                classes={classes}
              />
            ))
          ) : (
            <NoDataComponent />
          )}
        </Box>
      </Box>

      {/* Delete Modal */}
      <DeleteModal open={!!deleteGoalId} onClose={() => setDeleteGoalId(null)} onDelete={handleDeleteConfirm} />

      {/* Quarter Goal Drawer */}
      <QuarterGoalDrawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        goal={selectedGoal}
        isEditing={isEditing}
        onSave={handleDrawerSave}
        classes={classes}
        isAdminFlag={isAdminFlag}
      />
    </Container>
  );
};
